import { forwardRef } from "react";
import SubscriberFlag from "@components/ArticleMeta/SubscriberFlag";
import Summary from "@components/ArticleMeta/Summary";
import ArticleByline from "@pages/Article/components/ArticleByline/ArticleByline";
import BasicCardImage from "@particles/card/components/CardImage";
import BasicCardKicker from "@particles/card/components/CardKicker";
import BasicCardMeta from "@particles/card/components/CardMeta";
import BasicCardStoryThread from "@particles/card/components/CardStoryThread";
import BasicCardTime from "@particles/card/components/CardTime";
import BasicCardTitle from "@particles/card/components/CardTitle";
import { BasicCardProps } from "@particles/card/utils/types";
import { Type } from "@typings/enums";
import { cn, getDefaultImage } from "@util/helpers";

/**
 * BasicCard component that displays an article card with media, title, summary, and metadata.
 */
const Card = forwardRef<HTMLDivElement, BasicCardProps>(
  (
    {
      className = "",
      media,
      kicker = "",
      meta,
      title = "",
      blurb = "",
      editedTime = "",
      updatedTime = "",
      hasUpdatedTimestampDisplay = false,
      variations,
      defaultImage,
      isH1,
      isTitleLinkRequired = true,
      displayPlaceholderWhenImageLoading = false,
      storyThread,
      preloadLCPImage = false,
      link,
      displayPaywall = false,
      id,
      displayAsTopTitle = false,
      authors = [],
    },
    ref
  ): React.ReactElement => {
    const cardWrapperClass = [className];
    const imageWrapperClass = [
      variations?.image?.width,
      variations?.image?.imageWrapperClass,
      variations?.image?.extraClass,
    ];
    const contentWrapperClass = [
      "flex-1",
      variations?.content?.bgColor,
      variations?.content?.width,
    ];

    const isVideo =
      media?.type === Type.ArticleBrightcoveVideo ||
      media?.type === Type.ArticleYoutubeVideo;

    const defaultThumbnail = defaultImage
      ? {
          id: id,
          original: {
            url: getDefaultImage(defaultImage?.directoryName),
            width: 1570,
            height: 1046,
          },
          alt: title,
        }
      : undefined;

    const _media = media || defaultThumbnail;

    switch (variations?.image?.position) {
      case "left":
        imageWrapperClass.push("order-1 flex-shrink-0");
        contentWrapperClass.push("order-2");
        break;

      case "right":
        imageWrapperClass.push("order-2 flex-shrink-0");
        contentWrapperClass.push("order-1");
        break;
    }

    return (
      <div
        ref={ref}
        data-testid="basic-card-component"
        data-cueid={id}
        className={cn("relative flex flex-wrap items-start", cardWrapperClass)}
      >
        {displayAsTopTitle && kicker ? (
          <BasicCardKicker
            className={cn(
              "break-words font-poppins text-4xs font-light leading-4",
              variations?.kicker?.color,
              variations?.kicker?.extraClass,
              {
                "text-white": variations?.content?.bgColor,
              }
            )}
            text={kicker}
          />
        ) : null}

        {displayAsTopTitle ? (
          <BasicCardTitle
            title={title}
            articleId={id.toString()}
            to={isTitleLinkRequired ? link : undefined}
            className={cn(
              variations?.title?.size,
              variations?.title?.extraClass
            )}
            isH1={isH1}
            linkRootClassName={variations?.title?.color}
          />
        ) : null}

        {!_media && displayPlaceholderWhenImageLoading ? (
          <div className="hidden aspect-3x2 w-full bg-gray-850 lg:block"></div>
        ) : null}

        {_media ? (
          <BasicCardImage
            media={_media}
            className={cn("relative flex", variations?.image?.aspectRatio)}
            imageWrapperClassName={cn(imageWrapperClass)}
            imageClassName={variations?.image?.imageClassName}
            filters={variations?.image?.filters}
            link={link}
            orientation={variations?.image?.orientation}
            preloadLCPImage={preloadLCPImage}
            isVideo={isVideo}
            aspectRatio={variations?.image?.aspectRatio || "aspect-3x2"}
          />
        ) : null}

        <div
          className={cn(
            "flex flex-grow flex-col",
            contentWrapperClass,
            variations?.content?.extraClass,
            {
              "p-4 text-white": variations?.content?.bgColor,
            }
          )}
        >
          {storyThread ? (
            <BasicCardStoryThread
              storyThread={storyThread}
              className="mb-[.12rem]"
            />
          ) : null}

          {meta ? (
            <BasicCardMeta
              text={meta.text}
              to={meta.to}
              className={variations?.section?.extraClass}
            />
          ) : null}

          {!displayAsTopTitle && kicker ? (
            <BasicCardKicker
              className={cn(
                "break-words font-poppins text-4xs font-light leading-4",
                variations?.kicker?.color,
                variations?.kicker?.extraClass,
                {
                  "text-white": variations?.content?.bgColor,
                }
              )}
              text={kicker}
            />
          ) : null}

          {!displayAsTopTitle ? (
            <BasicCardTitle
              title={title}
              articleId={id.toString()}
              to={isTitleLinkRequired ? link : undefined}
              className={cn(
                variations?.title?.size,
                variations?.title?.extraClass
              )}
              isH1={isH1}
              linkRootClassName={variations?.title?.color}
            />
          ) : null}

          {displayPaywall ? (
            <SubscriberFlag
              rootClassName={variations?.subscriberLabel?.extraClass}
            />
          ) : null}

          {blurb ? (
            <Summary
              rootClassName={cn(
                "font-public-sans text-xs font-normal",
                variations?.content?.blurb?.extraClass,
                { "text-white": variations?.content?.bgColor }
              )}
              summary={blurb}
            />
          ) : null}

          {updatedTime || (editedTime && hasUpdatedTimestampDisplay) ? (
            <BasicCardTime
              updatedTime={updatedTime}
              editedTime={editedTime}
              createdClassName={variations?.content?.updatedTimeExtraClass}
              editedClassName={variations?.content?.updatedTimeColor}
              hasUpdatedTimestampDisplay={hasUpdatedTimestampDisplay}
            />
          ) : null}

          {authors.length > 0 ? (
            <ArticleByline
              authors={authors}
              updatedTime={updatedTime}
              byline={variations?.byline}
              variations={variations}
            />
          ) : null}
        </div>
      </div>
    );
  }
);

Card.displayName = "Card";

export default Card;
